import { action, computed, observable } from 'mobx'
import {
  IMembersSelectorGroup,
  IMembersSelectorMembers,
  IMembersSelectorUser,
  IMembersSelectorContext
} from '../MembersSelectors.types'
import { MIN_SEARCH_LEN } from '../constants'

const filterUnselectedMembers = <
  T extends IMembersSelectorUser | IMembersSelectorGroup
>(
  members: T[],
  selectedMembers: T[]
): T[] => {
  const selectedMembersIdsSet = new Set(selectedMembers.map((m) => m.id))

  return members.filter((m) => !selectedMembersIdsSet.has(m.id))
}
const memberIsNotSelected = <
  T extends IMembersSelectorUser | IMembersSelectorGroup
>(
  member: T,
  selectedMembers: T[]
): boolean => {
  return !selectedMembers.find((m) => m.id === member.id)
}

export class MemberSelectorState implements IMembersSelectorContext {
  changeDebouncedQuery: (q: string) => void
  @observable isMembersListShown = false
  @observable isAddressBookActive = false
  @observable query = ''
  @observable selectedMembers: IMembersSelectorMembers
  @observable addressBook: IMembersSelectorMembers | undefined
  @observable membersFound: IMembersSelectorMembers
  notifyParentOnChangeSelectedMembers: (
    members: IMembersSelectorMembers
  ) => void

  constructor({
    changeDebouncedQuery,
    selectedMembers,
    addressBook,
    membersFound,
    changeSelectedMembers: notifyParentOnChangeSelectedMembers
  }: {
    changeDebouncedQuery: (q: string) => void
    selectedMembers: IMembersSelectorMembers
    addressBook: IMembersSelectorMembers | undefined
    membersFound: IMembersSelectorMembers
    changeSelectedMembers: (members: IMembersSelectorMembers) => void
  }) {
    this.changeDebouncedQuery = changeDebouncedQuery
    this.addressBook = addressBook
    this.membersFound = membersFound
    this.selectedMembers = selectedMembers
    this.notifyParentOnChangeSelectedMembers =
      notifyParentOnChangeSelectedMembers
  }

  @action
  addUser = (user: IMembersSelectorUser) => {
    if (memberIsNotSelected(user, this.selectedMembers.users)) {
      this.changeSelectedMembers({
        users: [...this.selectedMembers.users, user],
        groups: this.selectedMembers.groups
      })
      this.reset()
    }
  }

  @action
  addGroup = (group: IMembersSelectorGroup) => {
    if (memberIsNotSelected(group, this.selectedMembers.groups)) {
      this.changeSelectedMembers({
        users: this.selectedMembers.users,
        groups: [...this.selectedMembers.groups, group]
      })
      this.reset()
    }
  }

  @action
  removeUser = (user: IMembersSelectorUser) => {
    this.changeSelectedMembers({
      users: this.selectedMembers.users.filter((u) => u.id !== user.id),
      groups: this.selectedMembers.groups
    })
    this.reset()
  }

  @action
  removeGroup = (group: IMembersSelectorGroup) => {
    this.changeSelectedMembers({
      users: this.selectedMembers.users,
      groups: this.selectedMembers.groups.filter((u) => u.id !== group.id)
    })
    this.reset()
  }

  @computed
  get autocompleteUsers() {
    return filterUnselectedMembers(
      (this.isAddressBookActive ? this.addressBook : this.membersFound)
        ?.users ?? [],
      this.selectedMembers.users
    )
  }

  @computed
  get autocompleteGroups() {
    return filterUnselectedMembers(
      (this.isAddressBookActive ? this.addressBook : this.membersFound)
        ?.groups ?? [],
      this.selectedMembers.groups
    )
  }

  @action
  setQuery = (query: string) => {
    this.query = query
    this.setShowMembersList(!!query)
    if (query.length > MIN_SEARCH_LEN) {
      this.changeDebouncedQuery(query)
    }
  }

  @action
  setShowMembersList = (show: boolean) => {
    this.isMembersListShown = show
  }

  @action
  setShowAddressBook = (show: boolean) => {
    this.isAddressBookActive = show
    this.setShowMembersList(show)
    this.query = ''
  }

  @action
  reset = () => {
    this.query = ''
    this.isAddressBookActive = false
    this.isMembersListShown = false
  }

  @action
  protected changeSelectedMembers = ({
    users: selectedUsers,
    groups: selectedGroups
  }: IMembersSelectorMembers) => {
    this.notifyParentOnChangeSelectedMembers({
      users: selectedUsers,
      groups: selectedGroups
    })
  }

  @action
  setSelectedMembers = (selectedMembers: IMembersSelectorMembers) => {
    this.selectedMembers = selectedMembers
  }

  @action
  setAddressBook = (addressBook?: IMembersSelectorMembers) => {
    this.addressBook = addressBook
  }

  @action
  setFoundMembers = (membersFound: IMembersSelectorMembers) => {
    this.membersFound = membersFound
  }

  setNotifyParentOnChangeSelectedMembers = (
    notifyParentOnChangeSelectedMembers: (
      members: IMembersSelectorMembers
    ) => void
  ) => {
    this.notifyParentOnChangeSelectedMembers =
      notifyParentOnChangeSelectedMembers
  }
}
